import { ConfirmationResult, User } from "firebase/auth";
import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { alertService } from "src/services";

interface ContactData {
  mobile_number: string;
}

interface Props {
  setShowLoader: React.Dispatch<React.SetStateAction<boolean>> | undefined;
  onVerificationSuccess: (user: User) => void;
  label: string;
  placeholder: string;
  type?: string;
  handleSendOtp?: (
    e?: React.FormEvent<HTMLFormElement> | undefined,
    number?: string,
  ) => Promise<void>;
  setRecaptcha?: () => Promise<void>;
  setErrorMessage?: React.Dispatch<React.SetStateAction<string>>;
  resendTimer: number;
  setResendTimer: (value: React.SetStateAction<number>) => void;
  confirmResult: ConfirmationResult | undefined;
  setConfirmResult: React.Dispatch<
    React.SetStateAction<ConfirmationResult | undefined>
  >;
  isLoading: boolean;
  errorMessage: string;
  resendCount: number;
  setData: React.Dispatch<React.SetStateAction<ContactData>>;
  data: ContactData;
  tosAccepted: boolean;
  showTosCheckbox: boolean;
  setHighlightTos: React.Dispatch<React.SetStateAction<boolean>>;
  tosRef: React.RefObject<HTMLInputElement | null>;
  isSocialLoading?: boolean;
}

export const VerifiedMobileInput = ({
  setShowLoader,
  onVerificationSuccess,
  label,
  placeholder,
  type,
  handleSendOtp,
  setRecaptcha,
  setErrorMessage,
  resendTimer,
  setResendTimer,
  confirmResult,
  setConfirmResult,
  isLoading,
  isSocialLoading,
  errorMessage,
  resendCount,
  setData,
  setHighlightTos,
  tosRef,
  data,
  tosAccepted,
  showTosCheckbox,
}: Props) => {
  const [verificationCode, setVerificationCode] = useState([
    "",
    "",
    "",
    "",
    "",
    "",
  ]);
  const [captchaVerified, setCaptchaVerified] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const onChange = (ev: ChangeEvent<HTMLInputElement>) => {
    if (ev) {
      const { name } = ev.currentTarget;
      let value = ev.currentTarget.value;
      if (name === "mobile_number") {
        setErrorMessage?.("");
        value = value.replace(/[^0-9]/g, "");
        if (value.length > 10) {
          setErrorMessage?.("");
          value = value.substring(0, 10);
        }
      }
      setData((prevData) => ({ ...prevData, [name]: value }));
    }
  };

  useEffect(() => {
    if (data.mobile_number.length === 10 && !captchaVerified) {
      setCaptchaVerified(false);
      handleSendOtp?.();
    }
  }, [data.mobile_number, captchaVerified]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        if (setShowLoader) setShowLoader(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    // Cleanup on unmount
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [setShowLoader]);

  useEffect(() => {
    if (resendTimer > 0) {
      const interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [resendTimer]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleVerifyCode = async (
    event:
      | React.FormEvent<HTMLFormElement>
      | React.MouseEvent<HTMLButtonElement, MouseEvent>
      | React.KeyboardEvent<HTMLInputElement>,
  ) => {
    event.preventDefault();

    if (loading) return;
    if (showTosCheckbox && !tosAccepted) {
      setHighlightTos(true);
      tosRef.current?.focus();
      setTimeout(() => setHighlightTos(false), 1000);
      return;
    }

    if (!confirmResult) {
      alertService.error("Please enter correct code.");
      return;
    }

    try {
      setLoading(true);
      if (setShowLoader) setShowLoader(true);

      const result = await confirmResult.confirm(verificationCode.join(""));
      const user = result.user;
      localStorage.setItem("isLoggedInWith", "Mobile");
      await onVerificationSuccess(user);

      if (type === "register") {
        setConfirmResult(undefined);
      }
    } catch {
      alertService.error("Please enter correct code.");
    } finally {
      setLoading(false);
      if (setShowLoader) setShowLoader(false);
    }
  };

  const handleBack = () => {
    setConfirmResult(undefined);
    setVerificationCode(["", "", "", "", "", ""]);
    setData({ mobile_number: "" });
    if (window.recaptchaVerifier) {
      window.recaptchaVerifier.clear();
      window.recaptchaVerifier = null;
    }
    setRecaptcha?.();
  };

  const handleCodeChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number,
  ) => {
    const value = e.target.value.replace(/\D/g, "");
    if (!value) {
      const newCode = [...verificationCode];
      newCode[index] = "";
      setVerificationCode(newCode);
      return;
    }

    const newCode = [...verificationCode];
    newCode[index] = value.slice(-1);
    setVerificationCode(newCode);

    if (index < 5) {
      document.getElementById(`code-${index + 1}`)?.focus();
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (e.key === "Backspace" && !verificationCode[index] && index > 0) {
      document.getElementById(`code-${index - 1}`)?.focus();
    }

    if (index === 5 && e.key === "Enter") {
      handleVerifyCode(e);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text").replace(/\D/g, "");

    if (!pasteData) return;

    const digits = pasteData.split("").slice(0, 6);
    const newCode = ["", "", "", "", "", ""];

    digits.forEach((digit, idx) => {
      newCode[idx] = digit;
    });

    setVerificationCode(newCode);
    const lastIndex = digits.length - 1;
    if (lastIndex >= 0 && lastIndex < 6) {
      document.getElementById(`code-${lastIndex}`)?.focus();
    }
  };

  return (
    <>
      <div className="mb-4 relative">
        <label
          htmlFor="mobile"
          className="block text-sm text-left font-medium text-gray-300 mb-2"
        >
          {label}
        </label>

        <div className="relative">
          <input
            type="text"
            id="mobile"
            name="mobile_number"
            required
            className={`w-full pr-10 pl-4 py-2 bg-gray-800 border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-white placeholder-gray-600 text-sm font-figtree ${
              isLoading || isSocialLoading
                ? "opacity-80 blur-[0.5px] cursor-not-allowed"
                : ""
            }`}
            placeholder={placeholder}
            value={data.mobile_number}
            onChange={onChange}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !captchaVerified) {
                e.preventDefault();
                handleSendOtp?.(e as unknown as FormEvent<HTMLFormElement>);
              }
            }}
            disabled={!!captchaVerified || isLoading || isSocialLoading}
          />
          {isLoading && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <div className="w-5 h-5 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}
        </div>

        {errorMessage && (
          <span className="text-red-500 text-sm mt-1">{errorMessage}</span>
        )}

        <div style={{ display: captchaVerified ? "none" : "block" }}>
          <div id="recaptcha-container"></div>
        </div>
      </div>

      {confirmResult && !isLoading && (
        <>
          <div className="mb-2">
            <label
              htmlFor="verification-code"
              className="block text-sm text-left font-medium text-gray-300 mb-2"
            >
              Verification Code
            </label>
            <div className="flex justify-center space-x-2">
              {[0, 1, 2, 3, 4, 5].map((index) => (
                <input
                  key={index}
                  id={`code-${index}`}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={1}
                  value={verificationCode[index]}
                  onChange={(e) => handleCodeChange(e, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  onPaste={handlePaste}
                  className="w-14 h-14 text-2xl text-center bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-white"
                  autoFocus={index === 0}
                />
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={
                (showTosCheckbox && !tosAccepted) ||
                verificationCode.some((code) => code === "") ||
                loading
              }
              className={`w-full py-3 px-4 rounded-lg text-white bg-gradient-to-t from-[#066766] to-[#0FA243] hover:opacity-90 ${
                (showTosCheckbox && !tosAccepted) ||
                verificationCode.some((code) => code === "") ||
                loading
                  ? "opacity-50 cursor-not-allowed"
                  : "cursor-pointer"
              }`}
              onClick={(event) => handleVerifyCode(event)}
            >
              {loading ? "Verifying..." : "Verify Code"}
            </button>

            <button
              type="button"
              className="w-full py-3 px-4 rounded-lg text-black bg-white hover:opacity-90 cursor-pointer"
              onClick={handleBack}
            >
              Back
            </button>
          </div>
          {/* {showTosCheckbox && !tosAccepted && (
            <p className="text-red-500 text-sm text-center mt-2">
              Please accept the terms and conditions to verify.
            </p>
          )} */}

          <div className="text-center text-white mb-2">
            {resendCount < 3 && resendTimer > 0 ? (
              <span className="cursor-pointer">
                Resend OTP in {resendTimer}s
              </span>
            ) : resendCount <= 3 ? (
              <button
                type="button"
                className="btn btn-link p-0 cursor-pointer"
                style={{
                  color: "#0F998E",
                  backgroundColor: "transparent",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                }}
                onClick={(_e) => {
                  setVerificationCode(["", "", "", "", "", ""]);
                  handleSendOtp?.({
                    preventDefault: () => {
                      console.log("resend otp called");
                    },
                  } as FormEvent<HTMLFormElement>);
                }}
              >
                Resend OTP
              </button>
            ) : (
              <span className="text-danger">
                You have reached all the attempts to resend OTP. Please try
                again later.
              </span>
            )}
          </div>
        </>
      )}
    </>
  );
};
